export type MappingReviewAction = 'accept' | 'dismiss' | 'reopen';

const STATUS_BY_ACTION: Record<MappingReviewAction, 'accepted' | 'dismissed' | 'pending'> = {
  accept: 'accepted',
  dismiss: 'dismissed',
  reopen: 'pending',
};

export const mappingReviewStatusOf = (action: MappingReviewAction): 'accepted' | 'dismissed' | 'pending' =>
  STATUS_BY_ACTION[action];

/** A card whose thumbs settle its AI Excel mappings rather than a field or a citation. */
export const isMappingSettleItem = (
  item: { canSettle?: boolean; canSettleCitation?: boolean; canSettleMapping?: boolean }
): boolean => Boolean(item.canSettleMapping && !item.canSettle && !item.canSettleCitation);

/** Stamp the settled status on the targeted rows; untouched lists keep their identity. */
export const applyMappingReviewToRefs = <T extends { id?: number | null }>(
  refs: T[] | undefined,
  mappingIds: number[],
  action: MappingReviewAction
): T[] | undefined => {
  const targets = new Set(mappingIds);
  if (!Array.isArray(refs) || !refs.some(ref => ref?.id != null && targets.has(ref.id))) {
    return refs;
  }
  const status = mappingReviewStatusOf(action);
  return refs.map(ref => (ref?.id != null && targets.has(ref.id) ? { ...ref, status } : ref));
};

type MappingCell = {
  mappingReferences?: { id?: number | null }[];
  dispatchEvent?: () => void;
};

type MappingSecurity = {
  mapping_references?: { id?: number | null }[];
};

export const applyMappingReviewToCell = (
  cell: MappingCell | null | undefined,
  mappingIds: number[],
  action: MappingReviewAction
) => {
  if (!cell || !Array.isArray(cell.mappingReferences)) {
    return;
  }
  // The spreadsheet already holds this cell object; write the settled rows onto it.
  // eslint-disable-next-line no-param-reassign
  cell.mappingReferences = applyMappingReviewToRefs(cell.mappingReferences, mappingIds, action);
  cell.dispatchEvent?.();
};

export const mapSecuritiesMappingReview = <T extends MappingSecurity>(
  securities: T[],
  mappingIds: number[],
  action: MappingReviewAction
): T[] =>
    securities.map(security => {
      const refs = applyMappingReviewToRefs(security.mapping_references, mappingIds, action);
      return refs === security.mapping_references ? security : { ...security, mapping_references: refs };
    });

type SettleMappingReviewParams = {
  mappingIds: number[];
  action: MappingReviewAction;
  persist: (mappingIds: number[], action: MappingReviewAction) => Promise<unknown>;
  cell?: MappingCell | null;
  patchSecurities: (updater: (securities: any[]) => any[]) => void;
};

/** Persist a mapping settle over the card's whole AI mapping batch and patch the cell and securities. */
export const settleMappingReview = async ({
  mappingIds,
  action,
  persist,
  cell,
  patchSecurities,
}: SettleMappingReviewParams): Promise<'accepted' | 'dismissed' | 'pending'> => {
  await persist(mappingIds, action);
  applyMappingReviewToCell(cell, mappingIds, action);
  patchSecurities(securities => mapSecuritiesMappingReview(securities, mappingIds, action));
  return mappingReviewStatusOf(action);
};
